import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import {
  buildHref, canonicalizeUrl, currentHref, joinList, parseList, parseLocation,
} from './urlState.js';

// Single owner of window.history for the whole app. Pages never call
// pushState themselves — they read `segments` / `query` and write through
// `navigate` / `setParams`, which keeps every URL canonical and keeps React
// in step with Back / Forward.
//
// Push vs replace:
//   push    — a real navigation the user should be able to Back out of
//             (top-level page, sub-tab, opening a record).
//   replace — refining what's on screen (typing in a search box, ticking a
//             gap chip, changing sort). These would otherwise bury the Back
//             button under one entry per keystroke.
// `replace` is the default for params and `push` the default for navigate.

const UrlContext = createContext(null);

export function UrlStateProvider({ children, knownPages }) {
  const [loc, setLoc] = useState(() => {
    canonicalizeUrl(knownPages);
    return parseLocation();
  });

  useEffect(() => {
    const onPop = () => setLoc(parseLocation());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Every write re-reads window.location instead of closing over `loc`.
  // Two setters firing in the same tick (a tab change that also clears its
  // filters, say) would otherwise have the second one rebuild the URL from a
  // snapshot taken before the first — silently dropping it.
  const apply = useCallback((segments, query, { replace = false } = {}) => {
    const href = buildHref(segments, query);
    if (href === currentHref()) return;
    const current = window.history.state?.psc_depth || 0;
    const depth = replace ? current : current + 1;
    const state = { ...(window.history.state || {}), psc_depth: depth };
    if (replace) window.history.replaceState(state, '', href);
    else window.history.pushState(state, '', href);
    setLoc(parseLocation());
  }, []);

  // Go to a page, replacing the query wholesale. Used for top-level nav and
  // for cross-page links that carry their own filters.
  const navigate = useCallback(
    (segments, query = {}, options = {}) => apply(segments, query, options),
    [apply],
  );

  // Patch query params on the current page. A null / '' / false value removes
  // the key, which is how "clear this filter" is expressed everywhere.
  const setParams = useCallback((patch, options = {}) => {
    const cur = parseLocation();
    const next = { ...cur.query };
    for (const [key, value] of Object.entries(patch || {})) {
      if (value === null || value === undefined || value === '' || value === false) {
        delete next[key];
      } else {
        next[key] = String(value);
      }
    }
    apply(cur.segments, next, { replace: true, ...options });
  }, [apply]);

  const back = useCallback(() => {
    if (typeof window !== 'undefined') window.history.back();
  }, []);

  const value = useMemo(() => ({
    segments: loc.segments,
    query: loc.query,
    // How many in-app navigations deep we are. 0 means Back would leave the
    // app, which is what disables the in-page Back buttons.
    depth: loc.depth,
    navigate,
    setParams,
    back,
  }), [loc, navigate, setParams, back]);

  return <UrlContext.Provider value={value}>{children}</UrlContext.Provider>;
}

export function useUrlState() {
  const ctx = useContext(UrlContext);
  if (!ctx) throw new Error('useUrlState must be used inside <UrlStateProvider>');
  return ctx;
}

// A single query param as a [value, setValue] pair.
//
// `active: false` detaches the hook from the URL: it keeps serving (and
// accepting) its own value, and publishes it back to the query string the
// moment it becomes active again. That's what lets the Rules page keep six
// explorers mounted while only the visible one owns `?q=`/`?cols=` — the
// hidden five neither read the visible tab's filters nor lose their own.
export function useUrlParam(key, fallback = '', options = {}) {
  const { active = true, replace = true } = options;
  const { query, setParams } = useUrlState();
  const [, forceRender] = useState(0);

  const urlValue = query[key] ?? fallback;
  // This hook's own idea of its value, independent of who owns the URL. A
  // hook that mounts inactive must NOT adopt whatever the visible tab has in
  // the query string under the same key.
  const ownRef = useRef(active ? urlValue : fallback);
  const wasActiveRef = useRef(active);
  // On the render that hands ownership back, the URL has been wiped by the
  // sub-tab navigation but our value hasn't — serve ours and let the effect
  // below publish it, so the table never flashes its defaults or refetches.
  const becameActive = active && !wasActiveRef.current;
  const value = active && !becameActive ? urlValue : ownRef.current;

  // One effect, and the order inside it matters: republish before adopting,
  // or the URL we are about to write would overwrite the value we're writing.
  useEffect(() => {
    const restoring = active && !wasActiveRef.current;
    wasActiveRef.current = active;
    if (!active) return;                    // detached — hold our own value
    if (restoring && ownRef.current !== urlValue) {
      setParams(
        { [key]: ownRef.current === fallback ? null : ownRef.current },
        { replace: true },
      );
      return;                               // the write comes back around as urlValue
    }
    ownRef.current = urlValue;
  }, [active, urlValue, key, fallback, setParams]);

  const setValue = useCallback((next) => {
    ownRef.current = next;
    if (active) setParams({ [key]: next === fallback ? null : next }, { replace });
    else forceRender((n) => n + 1);
  }, [active, key, fallback, replace, setParams]);

  return [value, setValue];
}

// Boolean flag param — present and 'true' means on, absent means off.
export function useUrlFlag(key, options = {}) {
  const [raw, setRaw] = useUrlParam(key, '', options);
  const value = raw === 'true' || raw === '1';
  const setValue = useCallback((next) => setRaw(next ? 'true' : ''), [setRaw]);
  return [value, setValue];
}

// Comma-separated list param exposed as a Set, plus the raw string so callers
// can put a stable value in a dependency array (a Set's identity changes every
// render and would re-fire every fetch).
export function useUrlSet(key, options = {}) {
  const [raw, setRaw] = useUrlParam(key, '', options);
  const value = useMemo(() => new Set(parseList(raw)), [raw]);
  const setValue = useCallback((nextSet) => {
    const arr = Array.from(nextSet || []);
    setRaw(arr.length === 0 ? '' : arr.join(','));
  }, [setRaw]);
  return [value, setValue, raw];
}

// Column-picker state. Absent means "untouched — use this table's defaults";
// an explicit empty list is stored as '-' so "I unticked everything" survives
// a page reload instead of snapping back to the defaults.
export function useUrlColumns(key, defaultCols, options = {}) {
  const [raw, setRaw] = useUrlParam(key, '', options);
  const value = useMemo(
    () => (raw ? new Set(parseList(raw)) : new Set(defaultCols)),
    [raw, defaultCols],
  );
  const setValue = useCallback((nextSet) => setRaw(joinList(nextSet)), [setRaw]);
  return [value, setValue];
}

// Search-box param. The input stays instant (local draft) while the URL — and
// therefore the API call — only moves once typing settles. Also means the page
// no longer needs a separate debounce on top of the URL value.
//
// Returns [draft, setDraft, committed]: render `draft`, fetch on `committed`.
export function useUrlDebouncedParam(key, options = {}) {
  const { delay = 300, ...paramOptions } = options;
  const [committed, setCommitted] = useUrlParam(key, '', paramOptions);
  const [draft, setDraft] = useState(committed);
  // Tracks what the draft has already been reconciled against, so the two
  // effects below can't ping-pong: one owns draft→URL, the other URL→draft.
  const syncedRef = useRef(committed);

  // URL changed from outside (Back / Forward, Clear All, a deep link).
  useEffect(() => {
    if (committed === syncedRef.current) return;
    syncedRef.current = committed;
    setDraft(committed);
  }, [committed]);

  // Typing settled — publish it.
  useEffect(() => {
    if (draft === committed) return undefined;
    const t = setTimeout(() => {
      syncedRef.current = draft;
      setCommitted(draft);
    }, delay);
    return () => clearTimeout(t);
  }, [draft, committed, delay, setCommitted]);

  return [draft, setDraft, committed];
}
